import { GetShareSettingsResponse } from '@orbit/api-types'
import { backgroundOrange } from '@/theme/colors'
import ConfirmationModal from '@components/ConfirmationModal/ConfirmationModal'
import Modal from '@components/Modal'
import { useDeleteTrip, useLeaveTrip, useShareSettings, useUpdateShareSettings } from '@hooks'
import { useUpdateTrip } from '@hooks/useUpdateTrip'
import { ButtonComponent, ButtonGroupComponent } from '@pages/Itinerary/AddEventModal/FormComponents'
import { isLeaveBlockedByBalances } from '@lib/apiError'
import { publicTripUrl } from '@lib/publicLinks'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import ChatMuteSection from './ChatMuteSection'
import JoinApprovalSection from './JoinApprovalSection'
import TripForm from './TripForm'
import { ModalFooter } from './EditTripModal.styles'
import SharingSection from './SharingSection'

interface EditTripModalProps {
	isOpen: boolean
	onClose: () => void
	tripId: string
	tripCode: string
	initialName: string
	initialCurrencyCode: string
	/** The trip's Join approval setting, shown to every member. */
	joinApprovalRequired?: boolean
	isOwner?: boolean
}

const EditTripModal: React.FC<EditTripModalProps> = ({
	isOpen,
	onClose,
	tripId,
	tripCode,
	initialName,
	initialCurrencyCode,
	joinApprovalRequired = false,
	isOwner,
}) => {
	const [name, setName] = useState(initialName)
	const [currencyCode, setCurrencyCode] = useState(initialCurrencyCode)
	const updateTrip = useUpdateTrip(tripId)
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const navigate = useNavigate()
	const deleteMutation = useDeleteTrip()
	const leaveMutation = useLeaveTrip()

	// Sharing is an owner-only surface (ADR 0003), so only fetch its settings for
	// owners while the modal is open.
	const canManageSharing = isOwner === true
	const shareSettingsQuery = useShareSettings(tripId, canManageSharing && isOpen)
	const updateShareSettings = useUpdateShareSettings(tripId)

	useEffect(() => {
		if (isOpen) {
			setName(initialName)
			setCurrencyCode(initialCurrencyCode)
		}
	}, [isOpen, initialName, initialCurrencyCode])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!name.trim()) return

		try {
			await updateTrip.mutateAsync({
				name: name.trim(),
				defaultCurrencyCode: currencyCode,
			})
			toast.success('Trip settings updated successfully!')
			onClose()
		} catch (error) {
			toast.error('Failed to update trip settings. Please try again.')
			console.error('Error updating trip:', error)
		}
	}

	const handleCancel = () => {
		setName(initialName)
		setCurrencyCode(initialCurrencyCode)
		onClose()
	}

	// Sharing toggles auto-save immediately (optimistic update + rollback in the
	// hook); they are independent of the form's Save/Cancel.
	const handleShareToggle = (field: keyof GetShareSettingsResponse, value: boolean) => {
		updateShareSettings.mutate(
			{ [field]: value },
			{
				onError: error => {
					toast.error('Failed to update sharing settings. Please try again.')
					console.error('Error updating share settings:', error)
				},
			},
		)
	}

	const handleDeleteClick = () => {
		setIsConfirmOpen(true)
	}

	const handleConfirmDelete = async () => {
		if (typeof isOwner === 'undefined') {
			setIsConfirmOpen(false)
			return
		}

		if (!isOwner) {
			leaveMutation.mutate(
				{ tripId },
				{
					onSuccess: () => {
						toast.success('Left trip')
						onClose()
						navigate('/trips')
					},
					onError: error => {
						console.error('Failed to leave trip', error)
						if (isLeaveBlockedByBalances(error)) {
							toast.error('Please delete or remove yourself from all expenses and pay ups first.')
						} else {
							toast.error('Failed to leave trip. Please try again.')
						}
					},
				},
			)
		} else {
			deleteMutation.mutate(
				{ tripId },
				{
					onSuccess: () => {
						toast.success('Trip deleted')
						onClose()
						navigate('/trips')
					},
					onError: error => {
						console.error('Failed to delete trip', error)
						toast.error('Failed to delete trip. Please try again.')
					},
				},
			)
		}

		setIsConfirmOpen(false)
	}

	const formId = 'edit-trip-form'
	const leftAction =
		typeof isOwner === 'undefined' ? null : (
			<ButtonComponent variant="danger" type="button" onClick={handleDeleteClick}>
				{!isOwner ? 'Leave trip' : 'Delete trip'}
			</ButtonComponent>
		)

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			title="Trip Settings"
			closeOnBackdropClick={false}
			backgroundColor={backgroundOrange}
			footer={
				<ModalFooter>
					<div>{leftAction}</div>
					<ButtonGroupComponent>
						<ButtonComponent type="button" variant="secondary" onClick={handleCancel}>
							Cancel
						</ButtonComponent>
						<ButtonComponent type="submit" form={formId} variant="primary" disabled={!name.trim() || updateTrip.isPending}>
							Save Changes
						</ButtonComponent>
					</ButtonGroupComponent>
				</ModalFooter>
			}
		>
			<TripForm
				formId={formId}
				name={name}
				currencyCode={currencyCode}
				onNameChange={setName}
				onCurrencyChange={setCurrencyCode}
				onSubmit={handleSubmit}
			/>

			<ChatMuteSection tripId={isOpen ? tripId : undefined} />

			{/* Every member sees it; only the owner flips it, and the backend enforces that too. */}
			{typeof isOwner !== 'undefined' && (
				<JoinApprovalSection
					tripId={tripId}
					tripName={initialName}
					isOwner={isOwner}
					joinApprovalRequired={joinApprovalRequired}
				/>
			)}

			{/* Public sharing is an owner-only decision (ADR 0003) — the section self-gates
			    on isOwner and the backend rejects non-owner mutations with a 403. */}
			<SharingSection
				isOwner={canManageSharing}
				settings={shareSettingsQuery.data}
				isLoading={shareSettingsQuery.isLoading}
				savingKeys={updateShareSettings.savingKeys}
				onToggle={handleShareToggle}
				publicUrl={publicTripUrl(tripCode)}
			/>

			<ConfirmationModal
				isOpen={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onConfirm={handleConfirmDelete}
				title={isOwner === false ? 'Leave Trip' : 'Delete Trip'}
				message={
					isOwner === false
						? 'Are you sure you want to leave this trip? You will no longer have access.'
						: 'Are you sure you want to delete this trip? This action cannot be undone.'
				}
				confirmText={isOwner === false ? 'Leave' : 'Delete'}
				cancelText="Cancel"
				variant="danger"
			/>
		</Modal>
	)
}

export default EditTripModal
